import { Box } from '@chakra-ui/react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

import { getTextSummary } from '../../api/build-a-dash-ai-gen-api';
import { ChartProvider } from '../Chart/ChartProvider';
import { ChartMenuDataProvider } from '../Chart/chart-menu/ChartMenuContext';
import type { ChartExportInfo } from '../Chart/chart-menu/chart-export-types';
import {
  buildOrderedPptExportCharts,
  countAuxiliaryPptBlocking,
  pageHasTextOrAiSummaryItems,
} from '../Chart/chart-menu/ordered-ppt-export-charts';
import { usePageExport } from '../Chart/chart-menu/PageExportProvider';
import { TextChartCardContentWithFilters } from '../Chart/TextChart/TextChartCardContent';
import { getFileName } from '../Chart/utils/getFileName';
import { MultiMetricDataProvider } from '../DataProvider/DataProvider';
import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../PageConfigProvider/constants';
import {
  usePageConfig,
  useTabContentConfig,
} from '../PageConfigProvider/PageConfigProvider';
import { PageType } from '../PageConfigProvider/types';
import type { PositionedItem } from '../PageConfigProvider/types/item';
import { useCoverSelectedParameters } from '../Reports/useCoverSelectedParameters';
import { useTextChartContent } from './TextChartContentProvider';

const useAiSummaryGenerator = () => {
  const { config } = useTabContentConfig();
  const { textCharts, getAllTextCharts, setAiSummary } = useTextChartContent();

  const previousContentHashRef = useRef<string | null>(null);

  const contentStateKey = useMemo(() => {
    const entries = Array.from(textCharts.entries()).map(([id, content]) => ({
      id,
      hasContent: content.content !== null,
      isLoading: content.isLoading,
      contentLength: content.content?.length ?? 0,
    }));
    return JSON.stringify(entries);
  }, [textCharts]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const hasAiSummaryChart = config.items.some(
      (item) => item.plot?.chartType === 'ai-summary'
    );

    if (!hasAiSummaryChart) {
      setAiSummary({
        summary: '',
        loading: false,
        error: null,
      });
      return;
    }

    const expectedTextItems = config.items.filter(
      (item) => item.plot?.chartType === 'text'
    );

    const expectedNonExcludedItems = expectedTextItems.filter((item) => {
      const shouldExclude = item.plot?.excludeFromExecutiveSummary ?? false;
      return !shouldExclude;
    });

    const textChartsList = getAllTextCharts();
    const chartsById = new Map(
      textChartsList.map((chart) => [chart.chartId, chart])
    );

    if (expectedNonExcludedItems.length === 0) {
      setAiSummary({
        summary:
          'No text charts found on this page. Add text charts to generate an AI summary.',
        loading: false,
        error: null,
      });
      return;
    }

    const allExpectedLoaded = expectedNonExcludedItems.every((item) => {
      const chart = chartsById.get(item.id);
      if (!chart) {
        return false;
      }
      return (
        !chart.content.isLoading &&
        (chart.content.content !== null || chart.content.error !== null)
      );
    });

    if (!allExpectedLoaded) {
      return;
    }

    const fetchSummary = async () => {
      try {
        setAiSummary((prev) => ({ ...prev, loading: true, error: null }));

        const currentTextCharts = getAllTextCharts();

        if (currentTextCharts.length === 0) {
          setAiSummary({
            summary: 'No text components found on this report.',
            loading: false,
            error: null,
          });
          return;
        }

        const itemsById = new Map(config.items.map((item) => [item.id, item]));

        const includedTexts = currentTextCharts
          .map((chart) => {
            if (chart.content.error) {
              return null;
            }

            const content = chart.content.content;
            if (!content || content.trim().length === 0) {
              return null;
            }

            const item = itemsById.get(chart.chartId);
            const shouldExclude =
              item?.plot?.excludeFromExecutiveSummary ?? false;

            if (shouldExclude) {
              return null;
            }

            return content;
          })
          .filter((text): text is string => text !== null);

        if (includedTexts.length === 0) {
          setAiSummary({
            summary: 'No text content found in text components.',
            loading: false,
            error: null,
          });
          return;
        }

        const contentHash = includedTexts.join('|||');
        if (contentHash === previousContentHashRef.current) {
          setAiSummary((prev) => ({ ...prev, loading: false }));
          return;
        }
        previousContentHashRef.current = contentHash;

        const combinedText = includedTexts.join('\n\n');

        const response = await getTextSummary({
          text: combinedText,
        });

        setAiSummary({
          summary: response.summary,
          loading: false,
          error: null,
        });
      } catch (err) {
        setAiSummary({
          summary: '',
          loading: false,
          error:
            err instanceof Error ? err.message : 'Failed to generate summary',
        });
      }
    };

    void fetchSummary();
  }, [getAllTextCharts, config.items, contentStateKey, setAiSummary]);
};

const useTextChartPptSync = () => {
  const { config, primaryFilter } = useTabContentConfig();
  const { config: pageConfig } = usePageConfig();
  const { textCharts, aiSummary } = useTextChartContent();
  const {
    setPptExportResolver,
    setPptAuxiliaryBlockingCount,
    setPageHasTextOrAiCharts,
    setReportExportMetadata,
    getChartRegistrySnapshot,
  } = usePageExport();
  const coverParameters = useCoverSelectedParameters();

  const layoutItems = config.items;
  const isReport = pageConfig.type === PageType.REPORT;
  const reportTitle = config.title ?? pageConfig.name ?? 'Report';
  const reportDescription = config.description;
  const gridRows = config.grid.rows;
  const gridColumns = config.grid.columns;

  const getTextExportForItem = useCallback(
    (item: PositionedItem): ChartExportInfo | null => {
      const html = item.plot?.textHtml?.trim();
      if (!html) {
        return null;
      }
      const entry = textCharts.get(item.id);
      if (!entry || entry.isLoading || entry.error !== null) {
        return null;
      }
      const text = entry.content?.trim() ?? '';
      if (!text.length) {
        return null;
      }
      const chartTitle = item.title ?? 'Text';
      return {
        title: chartTitle,
        defaultFileName: getFileName({
          primaryFilterId: primaryFilter?.id,
          pageName: pageConfig.name,
          chartTitle,
        }),
        dataType: 'text',
        textContent: entry.content ?? '',
      };
    },
    [textCharts, primaryFilter?.id, pageConfig.name]
  );

  const getAiExportForItem = useCallback(
    (item: PositionedItem, summaryBody: string): ChartExportInfo | null => {
      const title = item.title ?? 'Executive Summary';
      const html = item.plot?.textHtml?.trim();
      const textContent = html ? `${html}\n\n${summaryBody}` : summaryBody;
      if (!textContent.trim()) {
        return null;
      }
      return {
        title,
        defaultFileName: getFileName({
          primaryFilterId: primaryFilter?.id,
          pageName: pageConfig.name,
          chartTitle: title,
        }),
        dataType: 'text',
        textContent,
      };
    },
    [primaryFilter?.id, pageConfig.name]
  );

  useLayoutEffect(() => {
    setPageHasTextOrAiCharts(pageHasTextOrAiSummaryItems(layoutItems));

    setPptAuxiliaryBlockingCount(
      countAuxiliaryPptBlocking({
        items: layoutItems,
        textCharts,
        aiSummaryState: { loading: aiSummary.loading },
      })
    );

    setPptExportResolver(() => {
      return buildOrderedPptExportCharts({
        items: layoutItems,
        registry: getChartRegistrySnapshot(),
        aiSummaryState: aiSummary,
        getTextExportForItem,
        getAiExportForItem,
      });
    });

    if (isReport) {
      setReportExportMetadata({
        reportTitle,
        reportDescription,
        coverParameters,
        gridColumns: gridColumns || DEFAULT_GRID_COLUMNS,
        gridRows: gridRows || DEFAULT_GRID_ROWS,
        pageGridRows: DEFAULT_GRID_ROWS,
      });
    } else {
      setReportExportMetadata(null);
    }

    return () => {
      setPptExportResolver(null);
      setReportExportMetadata(null);
    };
  }, [
    aiSummary,
    coverParameters,
    getAiExportForItem,
    getChartRegistrySnapshot,
    getTextExportForItem,
    gridColumns,
    gridRows,
    isReport,
    layoutItems,
    reportDescription,
    reportTitle,
    setPageHasTextOrAiCharts,
    setPptAuxiliaryBlockingCount,
    setPptExportResolver,
    setReportExportMetadata,
    textCharts,
  ]);
};

const OffScreenTextChartsHydration = () => {
  const { config, primaryFilter } = useTabContentConfig();
  const { config: pageConfig } = usePageConfig();

  const textItems = config.items.filter(
    (item): item is PositionedItem => item.plot?.chartType === 'text'
  );

  if (textItems.length === 0) {
    return null;
  }

  return (
    <Box
      position="absolute"
      width="0"
      height="0"
      overflow="hidden"
      aria-hidden
      pointerEvents="none"
    >
      {textItems.map((item) => (
        <ChartProvider key={item.id} item={item}>
          <ChartMenuDataProvider>
            <MultiMetricDataProvider
              item={item}
              chartType="text"
              primaryDimension={primaryFilter?.id}
            >
              {({ data, status }) => (
                <TextChartCardContentWithFilters
                  data={data}
                  status={status}
                  item={item}
                  pageConfig={pageConfig}
                />
              )}
            </MultiMetricDataProvider>
          </ChartMenuDataProvider>
        </ChartProvider>
      ))}
    </Box>
  );
};

export const TextChartOrchestrator = () => {
  useAiSummaryGenerator();
  useTextChartPptSync();

  return <OffScreenTextChartsHydration />;
};
